'use client';

import { cn } from '@/lib/utils';

import Button, {
  AhrefElement,
  NextLinkElement,
} from '@/components/atoms/button';
import {
  getLinkRelFromConfig,
  useLinkConfig,
} from '@/components/LinkConfigProvider';
import { WithRef } from '@/components/types';

import { HTMLDataTrackerProps } from '@/app/types';
import IconChevronLeft from '~/svg/IconChevronLeft.svg';
import IconChevronRight from '~/svg/IconChevronRight.svg';
import IconExternal from '~/svg/IconExternal.svg';

export type LinkButtonInnerProps = {
  showLeftChevron?: boolean;
  showRightChevron?: boolean;
  showExternalIcon?: boolean;
  underline?: boolean;
  href: string;
  openInNewTab?: boolean;
  id?: number;
};

export type LinkButtonProps = (AhrefElement | NextLinkElement) &
  LinkButtonInnerProps &
  HTMLDataTrackerProps;

const LinkButton = ({
  children,
  variant = 'filled',
  slim = false,
  href,
  leftSection,
  rightSection,
  classNames,
  showLeftChevron,
  showRightChevron,
  showExternalIcon,
  openInNewTab,
  underline = true,
  ref,
  ...props
}: WithRef<HTMLAnchorElement, LinkButtonProps>) => {
  const linkConfig = useLinkConfig();
  const rel = getLinkRelFromConfig(linkConfig, href);

  if (!href) {
    console.log('LinkButton: href is not provided');
    return null;
  }

  const extraLeftSection = (showLeftChevron || leftSection) && (
    <>
      {showLeftChevron && <IconChevronLeft />}
      {leftSection}
    </>
  );

  const extraRightSection = (rightSection ||
    showExternalIcon ||
    showRightChevron) && (
    <>
      {rightSection}
      {showExternalIcon && <IconExternal />}
      {showRightChevron && <IconChevronRight />}
    </>
  );

  const isEmail = href?.includes('@');
  const customHref =
    isEmail && !href?.includes('mailto:') ? `mailto:${href}` : href;

  const isExternal = href?.match(/^(https?:)?\/\//);

  const innerOpenInNewTab =
    (isExternal && (openInNewTab === undefined || !!openInNewTab)) ||
    openInNewTab
      ? '_blank'
      : '_self';
  const isHash = href?.startsWith('#');
  const element = isExternal || isEmail || isHash ? 'a' : 'nextlink';

  return (
    <Button
      ref={ref}
      as={element}
      target={innerOpenInNewTab}
      href={customHref}
      variant={variant}
      slim={slim}
      classNames={{
        root: cn(
          'disabled:text-neutral-N50 font-sans',
          variant === 'text' && underline && 'hover:underline',
          (showRightChevron || showLeftChevron) && 'group',
          classNames?.root
        ),
        leftSection: cn(
          showLeftChevron &&
            'group-hover:-translate-x-2 duration-300 ease-in-out transition',
          classNames?.leftSection
        ),
        rightSection: cn(
          showRightChevron &&
            'group-hover:translate-x-2 duration-300 ease-in-out transition',
          classNames?.rightSection
        ),
        inner: classNames?.inner,
      }}
      leftSection={extraLeftSection}
      rightSection={extraRightSection}
      {...props}
      rel={rel}
    >
      {children}
    </Button>
  );
};

export default LinkButton;
